import { supabase } from '../lib/supabase';

export const securitySettingsService = {
  /**
   * Get all security settings for a role
   * @param {string} roleId - Role ID
   * @param {string} tenantId - Optional tenant ID for tenant-specific settings
   * @returns {Promise<Array>} Array of security settings
   */
  async getRoleSecuritySettings(roleId, tenantId = null) {
    const { data, error } = await supabase?.rpc('get_role_security_settings', {
      p_role_id: roleId,
      p_tenant_id: tenantId
    });

    if (error) throw error;

    return data?.map(setting => ({
      feature: setting?.feature,
      isAllowed: setting?.is_allowed,
      isTenantOverride: setting?.is_tenant_override,
      updatedBy: setting?.updated_by,
      updatedAt: setting?.updated_at
    })) || [];
  },

  /**
   * Get security settings matrix for all roles
   * @param {string} tenantId - Optional tenant ID
   * @returns {Promise<Object>} Matrix of role-feature permissions
   */
  async getSecurityMatrix(tenantId = null) {
    let query = supabase?.from('security_settings')?.select(`
      id,
      role_id,
      feature,
      is_allowed,
      tenant_id,
      updated_by,
      updated_at,
      system_roles (
        id,
        name,
        display_name,
        hierarchy_level
      )
    `);

    if (tenantId) {
      query?.or(`tenant_id.eq.${tenantId},tenant_id.is.null`);
    } else {
      query?.is('tenant_id', null);
    }

    const { data, error } = await query?.order('system_roles(hierarchy_level)', { ascending: true });

    if (error) throw error;

    // Transform to matrix structure
    const matrix = {};
    data?.forEach(setting => {
      const roleId = setting?.role_id;
      if (!matrix?.[roleId]) {
        matrix[roleId] = {
          roleId,
          roleName: setting?.system_roles?.name,
          roleDisplayName: setting?.system_roles?.display_name,
          hierarchyLevel: setting?.system_roles?.hierarchy_level,
          permissions: {}
        };
      }
      matrix[roleId].permissions[setting?.feature] = {
        isAllowed: setting?.is_allowed,
        isTenantOverride: setting?.tenant_id !== null,
        updatedBy: setting?.updated_by,
        updatedAt: setting?.updated_at
      };
    });

    return Object.values(matrix);
  },

  /**
   * Update single security setting
   * @param {string} roleId - Role ID
   * @param {string} feature - Feature name
   * @param {boolean} isAllowed - Whether feature is allowed
   * @param {string} tenantId - Optional tenant ID for tenant-specific override
   * @returns {Promise<Object>} Updated security setting
   */
  async updateSecuritySetting(roleId, feature, isAllowed, tenantId = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('security_settings')?.upsert({
        role_id: roleId,
        feature,
        is_allowed: isAllowed,
        tenant_id: tenantId,
        updated_by: user?.id
      }, {
        onConflict: 'role_id,feature,tenant_id'
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      roleId: data?.role_id,
      feature: data?.feature,
      isAllowed: data?.is_allowed,
      tenantId: data?.tenant_id,
      updatedBy: data?.updated_by,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Bulk update security settings for a role
   * @param {string} roleId - Role ID
   * @param {Array} settings - Array of {feature, isAllowed}
   * @param {string} tenantId - Optional tenant ID
   * @returns {Promise<void>}
   */
  async bulkUpdateSecuritySettings(roleId, settings, tenantId = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const settingsJson = settings?.map(s => ({
      feature: s?.feature,
      is_allowed: s?.isAllowed
    }));

    const { error } = await supabase?.rpc('bulk_update_security_settings', {
      p_role_id: roleId,
      p_settings: settingsJson,
      p_tenant_id: tenantId,
      p_updated_by: user?.id
    });

    if (error) throw error;
  },

  /**
   * Check if user has permission for a feature
   * @param {string} userId - User ID
   * @param {string} feature - Feature name
   * @returns {Promise<boolean>} Whether user has permission
   */
  async checkFeaturePermission(userId, feature) {
    const { data, error } = await supabase?.rpc('check_feature_permission', {
      p_user_id: userId,
      p_feature: feature
    });

    if (error) throw error;
    return data || false;
  },

  /**
   * Get all available features
   * @returns {Array<string>} Array of feature names
   */
  getAvailableFeatures() {
    return [
      'accounts',
      'contacts',
      'deals',
      'reports',
      'settings',
      'billing',
      'users',
      'integrations',
      'calendar',
      'services',
      'customers',
      'invoices',
      'notifications',
      'activities',
      'pipeline',
      'emails'
    ];
  },

  /**
   * Get feature display names
   * @returns {Object} Map of feature names to display names
   */
  getFeatureDisplayNames() {
    return {
      accounts: 'Accounts',
      contacts: 'Contacts',
      deals: 'Deals',
      reports: 'Reports',
      settings: 'Settings',
      billing: 'Billing',
      users: 'User Management',
      integrations: 'Integrations',
      calendar: 'Calendar',
      services: 'Services',
      customers: 'Customers',
      invoices: 'Invoices',
      notifications: 'Notifications',
      activities: 'Activities',
      pipeline: 'Pipeline',
      emails: 'Emails'
    };
  },

  /**
   * Reset security settings to defaults for a role
   * @param {string} roleId - Role ID
   * @param {string} tenantId - Optional tenant ID
   * @returns {Promise<void>}
   */
  async resetToDefaults(roleId, tenantId = null) {
    const { error } = await supabase?.from('security_settings')?.delete()?.eq('role_id', roleId)?.eq('tenant_id', tenantId);

    if (error) throw error;

    // Re-populate from role_permissions
    const { data: permissions } = await supabase?.from('role_permissions')?.select('feature, access_level')?.eq('role_id', roleId);

    const settings = this.getAvailableFeatures()?.map(feature => {
      const perm = permissions?.find(p => p?.feature === feature);
      return {
        feature,
        isAllowed: perm && perm?.access_level !== 'none'
      };
    });

    await this.bulkUpdateSecuritySettings(roleId, settings, tenantId);
  },

  /**
   * Copy security settings from one role to another
   * @param {string} sourceRoleId - Source role ID
   * @param {string} targetRoleId - Target role ID
   * @param {string} tenantId - Optional tenant ID
   * @returns {Promise<void>}
   */
  async copySecuritySettings(sourceRoleId, targetRoleId, tenantId = null) {
    const sourceSettings = await this.getRoleSecuritySettings(sourceRoleId, tenantId);
    
    const settings = sourceSettings?.map(s => ({
      feature: s?.feature,
      isAllowed: s?.isAllowed
    }));

    await this.bulkUpdateSecuritySettings(targetRoleId, settings, tenantId);
  },

  /**
   * Get security settings audit history
   * @param {string} roleId - Optional role ID filter
   * @param {number} limit - Number of records to return
   * @returns {Promise<Array>} Array of audit log entries
   */
  async getSecurityAuditHistory(roleId = null, limit = 50) {
    let query = supabase?.from('audit_logs')?.select(`
        id,
        action_type,
        action_description,
        performed_by,
        affected_role_id,
        before_state,
        after_state,
        change_details,
        created_at,
        user_profiles!audit_logs_performed_by_fkey (
          id,
          email,
          first_name,
          last_name
        ),
        system_roles!audit_logs_affected_role_id_fkey (
          id,
          name,
          display_name
        )
      `)?.eq('action_type', 'security_setting_updated')?.order('created_at', { ascending: false })?.limit(limit);

    if (roleId) {
      query = query?.eq('affected_role_id', roleId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(log => ({
      id: log?.id,
      actionType: log?.action_type,
      description: log?.action_description,
      performedBy: {
        id: log?.user_profiles?.id,
        email: log?.user_profiles?.email,
        fullName: `${log?.user_profiles?.first_name} ${log?.user_profiles?.last_name}`
      },
      affectedRole: {
        id: log?.system_roles?.id,
        name: log?.system_roles?.name,
        displayName: log?.system_roles?.display_name
      },
      beforeState: log?.before_state,
      afterState: log?.after_state,
      changeDetails: log?.change_details,
      createdAt: log?.created_at
    })) || [];
  }
};